import { useRef, useState, type DragEvent, type ReactNode } from "react";
import { RECEIVED_MSG, RECEIVED_PHONE_ACCEPT } from "@genbaryoku/contract";

/** どう入れたか（受け取った書類の x-gb-source・外注見積の source に写す） */
export type FileDropSource = "drop" | "upload" | "mobile_camera";

const hasFiles = (e: DragEvent): boolean => Array.from(e.dataTransfer?.types ?? []).includes("Files");

/**
 * ファイルを落とす所（../eigyouryoku/docs/13 決めごと13）。共通の部品：受け取った書類の枠と、{vendorQuote}の「登録する」が使う。
 *   ・点線の領域に落とす（複数可）＋ 入力欄の <label> の「ファイル選択」（display:none の input を click() で開かない＝Safari。docs/10 §5.10）。
 *     ドラッグだけの操作にしない（WCAG 2.2 SC 2.5.7）＝「ファイル選択」をいつも並べる
 *   ・乗せているあいだは .is-over（淡い藍の地）。dragenter／dragover／drop は自前（ライブラリを入れない）
 *   ・落とした・選んだファイルを onFiles で返すだけ（送るのは呼ぶ側）。落としたものは画面全体の重ね（lib/dropOverlay.ts）に渡さない
 *   ・スマホ版（html[data-view="phone"]）は点線の文を出さず、「ファイル選択」を 56px・幅いっぱいに（CSS）。accept は写真も選べる形に
 *   ・camera を渡すと「撮影」（capture）も並べる（{vendorQuote}の登録。撮った写真は mobile_camera）
 */
export function FileDrop({
  onFiles,
  accept,
  phoneAccept = RECEIVED_PHONE_ACCEPT,
  multiple = true,
  hint,
  chooseLabel = RECEIVED_MSG.choose,
  compact = false,
  disabled = false,
  testId,
  inputTestId,
  camera,
  extra,
  before,
  bare = false,
  hideButton = false,
}: {
  onFiles: (files: File[], source: FileDropSource) => void;
  /** PC の「ファイル選択」の形式 */
  accept: string;
  /** スマホ版の形式（写真を撮る／ライブラリ／ファイル が出る形） */
  phoneAccept?: string;
  multiple?: boolean;
  /** 点線の中の文（スマホ版は出さない） */
  hint: ReactNode;
  chooseLabel?: string;
  /** 1件以上あるときの低い枠 */
  compact?: boolean;
  disabled?: boolean;
  testId?: string;
  inputTestId?: string;
  camera?: { label: string; testId?: string };
  /** 右に並べるもの（文字だけのボタンなど） */
  extra?: ReactNode;
  /** 「ファイル選択」の前に並べるもの（見積依頼書の「受け取った書類から選択」＝②改 の並び） */
  before?: ReactNode;
  /** 点線の枠を描かない（帯の1段をそのまま落とす所にする。乗せているあいだだけ淡い藍の地と点線＝見積依頼書の添付の段） */
  bare?: boolean;
  /** 中の「ファイル選択」を出さない（受け取った書類の PC版は枠の右上に置く。落とすのはこの枠のまま） */
  hideButton?: boolean;
}) {
  const [over, setOver] = useState(false);
  const depth = useRef(0);
  const phone = typeof document !== "undefined" && document.documentElement.dataset.view === "phone";
  const take = (list: FileList | null | undefined, source: FileDropSource) => {
    const files = Array.from(list ?? []);
    if (!files.length || disabled) return;
    onFiles(multiple ? files : files.slice(0, 1), source);
  };
  return (
    <div
      className={"filedrop" + (over ? " is-over" : "") + (compact ? " is-compact" : "") + (bare ? " is-bare" : "") + (disabled ? " is-disabled" : "")}
      data-testid={testId}
      onDragEnter={(e) => {
        if (!hasFiles(e)) return;
        e.preventDefault();
        depth.current += 1;
        setOver(true);
      }}
      onDragOver={(e) => {
        if (!hasFiles(e)) return;
        e.preventDefault();
        e.dataTransfer.dropEffect = disabled ? "none" : "copy";
      }}
      onDragLeave={(e) => {
        if (!hasFiles(e)) return;
        depth.current = Math.max(0, depth.current - 1);
        if (depth.current === 0) setOver(false);
      }}
      onDrop={(e) => {
        if (!hasFiles(e)) return;
        e.preventDefault();
        e.stopPropagation(); // 画面全体の重ね（window の受け口）に二度渡さない
        depth.current = 0;
        setOver(false);
        take(e.dataTransfer.files, "drop");
      }}
    >
      <span className="fdhint">{hint}</span>
      <span className="fdbtns">
        {before}
        {hideButton ? null : (
          <label className={"btn fdpick" + (disabled ? " disabled" : "")}>
            {chooseLabel}
            <input
              type="file"
              className="vh"
              accept={phone ? phoneAccept : accept}
              multiple={multiple}
              disabled={disabled}
              data-testid={inputTestId}
              onChange={(e) => {
                const list = e.target.files;
                take(list, "upload");
                e.target.value = "";
              }}
            />
          </label>
        )}
        {camera ? (
          <label className={"btn fdpick" + (disabled ? " disabled" : "")}>
            {camera.label}
            <input
              type="file"
              className="vh"
              accept="image/*"
              capture="environment"
              disabled={disabled}
              data-testid={camera.testId}
              onChange={(e) => {
                take(e.target.files, "mobile_camera");
                e.target.value = "";
              }}
            />
          </label>
        ) : null}
        {extra}
      </span>
    </div>
  );
}
